/**
 * @fileoverview Regional Popup Modal Component
 * 
 * Displays a mandatory regional selector popup to international visitors on their first visit
 * to the default market. The popup detects the visitor's location using Shopify Markets
 * geolocation API and prompts them to either redirect to their regional market or continue
 * browsing the default market.
 * 
 * Features:
 * - Geolocation detection via Shopify's browsing_context_suggestions.json API
 * - Automatic redirect to preferred market on subsequent visits
 * - Cookie/localStorage persistence for user preferences
 * - WCAG 2.1 AA compliant accessibility features
 * - Dynamic text content with region name placeholders
 * - Theme Editor integration for customizable content
 * 
 * @see https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
 * 
 * @class RegionalPopupModal
 * @extends HTMLElement
 * 
 * @example
 * // The component is automatically initialized when the custom element is defined:
 * // <regional-popup-modal
 * //   data-default-market="US"
 * //   data-cookie-expiration="30"
 * //   data-heading="Shop [[detected_region]]"
 * //   ...
 * // ></regional-popup-modal>
 */

class RegionalPopupModal extends HTMLElement {
  /**
   * Create a new RegionalPopupModal instance
   * 
   * Initializes the component with settings from data attributes and begins
   * the initialization process.
   * 
   * @constructor
   * @param {Object} dataset - Data attributes from the HTML element:
   * @param {string} [dataset.defaultMarket='US'] - Default market country code
   * @param {string} [dataset.cookieExpiration='30'] - Cookie expiration in days
   * @param {string} [dataset.heading] - Popup heading template
   * @param {string} [dataset.bodyText] - Popup body text template
   * @param {string} [dataset.buttonGoToRegion] - "Go to Region" button text template
   * @param {string} [dataset.buttonBrowseGlobal] - "Browse Global" button text
   * @param {string} [dataset.closeLabel] - Close button accessible label
   */
  constructor() {
    super();
    this.cookieName = 'regional_popup_dismissed';
    this.defaultMarket = this.dataset.defaultMarket || 'US';
    this.cookieExpiration = parseInt(this.dataset.cookieExpiration) || 30;
    this.headingTemplate = this.dataset.heading || 'Shop [[detected_region]]';
    this.bodyTextTemplate = this.dataset.bodyText || 'You\'re visiting from [[detected_region]]. Would you like to shop the [[detected_region]] store?';
    this.buttonGoToRegionTemplate = this.dataset.buttonGoToRegion || 'GO TO [[detected_region]]';
    this.buttonBrowseGlobal = this.dataset.buttonBrowseGlobal || 'BROWSE GLOBAL';
    this.closeLabel = this.dataset.closeLabel || 'Close regional selector';
    
    this.detectedCountry = null;
    this.detectedRegionName = null;
    
    // Market data from Shopify Markets
    this.marketsData = window.__REGIONAL_POPUP_MARKETS || { availableCountries: [], defaultMarket: 'US' };
    
    // Dynamic mappings built from Shopify Markets data
    this.regionDisplayNameMap = {};
    this.countryToLocaleMap = {};
    
    // Track elements that were made inert for restoration
    this.inertElements = [];
    
    // Store element that had focus before modal opened
    this.previouslyFocusedElement = null;
    
    // Bind methods for event listeners
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    
    this.init();
  }

  /**
   * Initialize the regional popup modal
   * 
   * Checks if popup should be displayed, handles automatic redirects for returning visitors,
   * and shows the popup if the visitor is outside the default market.
   * 
   * @method init
   * @async
   * @returns {Promise<void>}
   */
  async init() {
    // Don't show in Theme Editor preview
    if (Shopify.designMode) {
      return;
    }

    // Build dynamic mappings from Shopify Markets data (needed for redirect)
    await this.buildMarketMappings();

    // Check if user has a stored preference
    const preference = this.getPreference();
    
    // If user previously selected a region, check if we need to redirect
    if (preference && preference !== 'browse_global') {
      const preferredCountryCode = preference.toUpperCase();
      
      // Verify the preferred country is still available
      const isAvailableMarket = this.marketsData.availableCountries.some(
        country => country.code.toUpperCase() === preferredCountryCode
      );
      
      if (isAvailableMarket) {
        // Check if we're already on the preferred market
        const currentLocale = this.getCurrentLocaleFromPath();
        const preferredLocale = this.getLocaleCodeForCountry(preferredCountryCode);
        
        // Normalize both locales to lowercase for comparison
        const normalizedCurrentLocale = currentLocale ? currentLocale.toLowerCase() : null;
        const normalizedPreferredLocale = preferredLocale ? preferredLocale.toLowerCase() : null;
        
        // Only redirect if we're not already on the preferred market
        // Also check if pathname already starts with the preferred locale (handles trailing slash differences)
        const pathname = window.location.pathname.toLowerCase();
        const isAlreadyOnPreferredMarket = normalizedCurrentLocale === normalizedPreferredLocale ||
          (normalizedPreferredLocale && pathname.startsWith(`/${normalizedPreferredLocale}`));
        
        if (!isAlreadyOnPreferredMarket) {
          // Build and redirect to preferred market
          const marketUrl = this.buildMarketUrl(preferredCountryCode);
          if (marketUrl && marketUrl !== window.location.href) {
            window.location.href = marketUrl;
            return; // Exit early, redirect will happen
          }
        } else {
          // Already on preferred market, don't show popup
          return;
        }
      }
    }

    // IMPORTANT: Only show popup on default market (US)
    // If user is already on a regional market (en-ca, en-gb, en-eu), don't show
    if (!this.isOnDefaultMarket()) {
      return;
    }

    // Check if popup should be shown (cookie check)
    if (!this.shouldShowPopup()) {
      return;
    }

    // Fetch geolocation data
    await this.detectVisitorLocation();
    
    // Show popup if visitor is outside default market
    if (this.shouldDisplayPopup()) {
      this.setupEventListeners();
      
      // CRITICAL: Replace placeholders BEFORE showing modal to prevent WCAG 4.1.2 violation
      // Screen readers must never encounter placeholder text
      const placeholdersReplaced = this.updatePopupContent();
      
      if (!placeholdersReplaced) {
        // If placeholders couldn't be replaced, don't show modal
        // This prevents screen readers from announcing placeholder text
        console.warn('Regional Popup: Cannot display modal - placeholders not replaced');
        return;
      }
      
      // Only show modal after confirming placeholders are replaced
      this.show();
    }
  }

  /**
   * Check if the current page is on the default market (no locale subdirectory)
   * 
   * @method isOnDefaultMarket
   * @returns {boolean} True if on default market, false if on regional market
   */
  isOnDefaultMarket() {
    // Check if we're on the default market by examining the URL path
    // Default market (US) has no locale subdirectory
    // Regional markets have subdirectories like /en-ca/, /en-gb/, /en-eu/
    const pathname = window.location.pathname;
    
    // Check if pathname starts with a locale subdirectory pattern
    // Pattern: /en-ca/, /en-gb/, /en-eu/, etc. (with or without trailing slash)
    const localePattern = /^\/(en-[a-z]{2}|[a-z]{2})(\/|$)/i;
    
    if (localePattern.test(pathname)) {
      // We're on a regional market, not default
      return false;
    }
    
    // We're on the default market (US)
    return true;
  }

  /**
   * Extract the current locale code from the URL pathname
   * 
   * @method getCurrentLocaleFromPath
   * @returns {string|null} Locale code (e.g., 'en-gb', 'en-ca') or null if on default market
   */
  getCurrentLocaleFromPath() {
    // Extract current locale from URL path
    // Handles both /en-gb/ and /en-gb patterns
    const pathname = window.location.pathname;
    const localeMatch = pathname.match(/^\/(en-[a-z]{2}|[a-z]{2})(\/|$)/i);
    
    if (localeMatch) {
      return localeMatch[1].toLowerCase();
    }
    
    // No locale prefix means default market
    return null;
  }

  async buildMarketMappings() {
    // Build region display name map from available countries
    this.marketsData.availableCountries.forEach(country => {
      const countryCode = country.code.toUpperCase();
      // Use country name from Shopify Markets, or fallback to country code
      this.regionDisplayNameMap[countryCode] = country.code.toUpperCase();
    });

    // Build locale code mappings based on country codes
    // Note: browsing_context_suggestions.json doesn't directly provide locale codes (en-ca, en-gb, en-eu)
    // We infer them based on country codes using our fallback mapping
    // The API is primarily used for detecting visitor location, not locale mapping

    // Fallback: Build locale mappings based on common patterns
    // This ensures we have mappings even if browsing_context_suggestions doesn't provide them
    this.marketsData.availableCountries.forEach(country => {
      const countryCode = country.code.toUpperCase();
      
      // Only set if not already set from browsing_context_suggestions
      if (!this.countryToLocaleMap[countryCode]) {
        this.countryToLocaleMap[countryCode] = this.inferLocaleCode(countryCode);
      }
    });
  }

  inferLocaleCode(countryCode) {
    // Infer locale code based on country code patterns
    // This is a fallback when browsing_context_suggestions doesn't provide locale info
    const localeMap = {
      'CA': 'en-ca',
      'GB': 'en-gb',
      // EU countries typically use en-eu
      'DE': 'en-eu', 'FR': 'en-eu', 'IT': 'en-eu', 'ES': 'en-eu',
      'NL': 'en-eu', 'BE': 'en-eu', 'AT': 'en-eu', 'CH': 'en-eu',
      'SE': 'en-eu', 'NO': 'en-eu', 'DK': 'en-eu', 'FI': 'en-eu',
      'IE': 'en-eu', 'PT': 'en-eu', 'PL': 'en-eu', 'GR': 'en-eu',
      'CZ': 'en-eu', 'HU': 'en-eu', 'RO': 'en-eu', 'BG': 'en-eu',
      'HR': 'en-eu', 'SK': 'en-eu', 'SI': 'en-eu', 'LT': 'en-eu',
      'LV': 'en-eu', 'EE': 'en-eu'
    };
    
    return localeMap[countryCode] || null;
  }

  /**
   * Detect visitor's location using Shopify Markets geolocation API
   * 
   * Uses browsing_context_suggestions.json endpoint to detect the visitor's country
   * based on their IP address and browser settings.
   * 
   * @method detectVisitorLocation
   * @async
   * @returns {Promise<void>}
   * @see https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
   */
  async detectVisitorLocation() {
    try {
      // Use Shopify's recommended API parameters for browsing context suggestions
      // https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
      const currentCountry = window.Shopify?.country || '';
      const currentLanguage = window.Shopify?.language || '';
      
      const queryParams = new URLSearchParams({
        'country[enabled]': 'true',
        'language[enabled]': 'true'
      });
      
      if (currentCountry) {
        queryParams.append('country[exclude]', currentCountry);
      }
      if (currentLanguage) {
        queryParams.append('language[exclude]', currentLanguage);
      }
      
      const response = await fetch(
        window.Shopify.routes.root + 'browsing_context_suggestions.json?' + queryParams.toString()
      );
      const data = await response.json();
      
      if (data?.detected_values?.country?.handle) {
        this.detectedCountry = data.detected_values.country.handle.toUpperCase();
        this.detectedRegionName = this.getRegionDisplayName(this.detectedCountry);
      }
    } catch (error) {
      console.warn('Regional Popup: Could not detect visitor location', error);
    }
  }

  getRegionDisplayName(countryCode) {
    // Use dynamic mapping from Shopify Markets, fallback to country code
    return this.regionDisplayNameMap[countryCode] || countryCode;
  }

  shouldShowPopup() {
    // Check cookie/localStorage
    const preference = this.getPreference();
    
    if (preference) {
      // If user already made a choice, don't show popup
      return false;
    }

    // Check Do Not Track
    if (navigator.doNotTrack === '1') {
      // Still show popup but respect their choice when they select
      return true;
    }

    return true;
  }

  shouldDisplayPopup() {
    if (!this.detectedCountry) {
      return false;
    }

    const defaultMarketUpper = this.defaultMarket.toUpperCase();
    
    // If detected country matches default market (US), don't show popup
    if (this.detectedCountry === defaultMarketUpper) {
      return false;
    }

    // Only show popup if the detected country is in the available markets
    // This ensures we only show popup for countries that have a market configured
    const isAvailableMarket = this.marketsData.availableCountries.some(
      country => country.code.toUpperCase() === this.detectedCountry
    );

    if (!isAvailableMarket) {
      return false;
    }

    // Show popup if detected country differs from default market
    // (We already checked we're on default market in isOnDefaultMarket())
    return true;
  }

  getPreference() {
    // Try cookie first
    if (typeof Cookies !== 'undefined') {
      const cookieValue = Cookies.get(this.cookieName);
      if (cookieValue) {
        return cookieValue;
      }
    }

    // Fallback to localStorage
    try {
      return localStorage.getItem(this.cookieName);
    } catch (e) {
      return null;
    }
  }

  setPreference(value) {
    // Try cookie first
    if (typeof Cookies !== 'undefined') {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + this.cookieExpiration);
      Cookies.set(this.cookieName, value, { expires: this.cookieExpiration });
    }

    // Also set in localStorage as backup
    try {
      localStorage.setItem(this.cookieName, value);
    } catch (e) {
      console.warn('Regional Popup: Could not set localStorage', e);
    }
  }

  /**
   * Update popup content by replacing all placeholder text with detected region name
   * 
   * WCAG 4.1.2 Compliance: Ensures placeholders are replaced BEFORE modal becomes visible
   * to prevent screen readers from announcing placeholder text.
   * 
   * @method updatePopupContent
   * @returns {boolean} True if placeholders were successfully replaced, false otherwise
   */
  updatePopupContent() {
    // Use detected region name, or fallback to a safe default if detection failed
    // This ensures placeholders are ALWAYS replaced, even if region detection fails
    const regionDisplayName = this.detectedRegionName || this.detectedCountry || 'your region';
    
    // Replace placeholders in heading
    const heading = this.querySelector('#RegionalPopupHeading');
    if (heading) {
      heading.textContent = this.headingTemplate.replace(/\[\[\s*detected_region\s*\]\]/gi, regionDisplayName);
    }

    // Replace placeholders in description
    const description = this.querySelector('#RegionalPopupDescription');
    if (description) {
      description.textContent = this.bodyTextTemplate.replace(/\[\[\s*detected_region\s*\]\]/gi, regionDisplayName);
    }

    // Replace placeholders in button
    const goToRegionButton = this.querySelector('[data-action="go-to-region"]');
    if (goToRegionButton) {
      goToRegionButton.textContent = this.buttonGoToRegionTemplate.replace(/\[\[\s*detected_region\s*\]\]/gi, regionDisplayName);
    }
    
    // Validate that all placeholders have been replaced
    return this.validatePlaceholdersReplaced();
  }

  /**
   * Validate that all placeholder text has been replaced
   * 
   * Checks all text content in the modal to ensure no placeholder tokens remain.
   * This prevents WCAG 4.1.2 violations where screen readers would announce placeholder text.
   * 
   * @method validatePlaceholdersReplaced
   * @returns {boolean} True if no placeholders found, false if placeholders remain
   */
  validatePlaceholdersReplaced() {
    // Get all text content from the modal
    const modalText = this.textContent || '';
    
    // Check for placeholder patterns (case-insensitive)
    const placeholderPattern = /\[\[\s*detected_region\s*\]\]/i;
    const hasPlaceholders = placeholderPattern.test(modalText);
    
    if (hasPlaceholders) {
      console.error('Regional Popup: Placeholder text not replaced! Modal will not be shown to prevent WCAG violation.');
      return false;
    }
    
    return true;
  }

  setupEventListeners() {
    // Close button
    const closeButton = this.querySelector('#RegionalPopupClose');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.handleBrowseGlobal());
    }

    // Go to region button
    const goToRegionButton = this.querySelector('[data-action="go-to-region"]');
    if (goToRegionButton) {
      goToRegionButton.addEventListener('click', () => this.handleGoToRegion());
    }

    // Browse global button
    const browseGlobalButton = this.querySelector('[data-action="browse-global"]');
    if (browseGlobalButton) {
      browseGlobalButton.addEventListener('click', () => this.handleBrowseGlobal());
    }

    // Tab key handling (focus trap) - attached to modal element
    // Note: keydown is used for Tab to prevent default before focus moves
    this.addEventListener('keydown', this.handleKeydown);
    
    // Escape key handling - attached to document for WCAG 2.1.1 compliance
    // This ensures Escape key works regardless of where focus is located
    document.addEventListener('keydown', this.handleDocumentKeydown);

    // Click outside to close (same as browse global)
    this.addEventListener('click', (e) => {
      if (e.target === this) {
        this.handleBrowseGlobal();
      }
    });
  }

  /**
   * Handle document-level keyboard events for Escape key
   * 
   * WCAG 2.1.1 Compliance: Allows closing modal with Escape key regardless of focus location
   * This handler is attached to the document to ensure Escape key works even if focus
   * is not within the modal element itself.
   * 
   * @method handleDocumentKeydown
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleDocumentKeydown(e) {
    // Only handle Escape key when modal is open
    if (!this.hasAttribute('open')) {
      return;
    }

    // Handle Escape key to close modal
    if (e.key === 'Escape' || e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.handleBrowseGlobal();
    }
  }

  /**
   * Handle keyboard events for focus trap (Tab key)
   * 
   * WCAG 2.4.3 Compliance: Traps focus within modal using Tab key cycling
   * This handler is attached to the modal element itself to handle Tab key navigation
   * within the modal.
   * 
   * @method handleKeydown
   * @param {KeyboardEvent} e - Keyboard event
   */
  handleKeydown(e) {
    // Only handle keys when modal is open
    if (!this.hasAttribute('open')) {
      return;
    }

    // Handle Tab key for focus trap
    if (e.key === 'Tab' || e.code === 'Tab') {
      const focusableElements = this.getFocusableElements();
      
      // If no focusable elements, prevent default tab behavior
      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      // Shift+Tab on first element -> focus last element
      if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Tab on last element -> focus first element
      else if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
      // If focus is outside modal, bring it back to first element
      else if (!focusableElements.includes(activeElement)) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Get all focusable elements within the modal
   * 
   * @method getFocusableElements
   * @returns {Array<HTMLElement>} Array of focusable elements
   */
  getFocusableElements() {
    // Use same selector as global.js getFocusableElements for consistency
    const selector = 'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe';
    return Array.from(this.querySelectorAll(selector)).filter(el => {
      // Filter out elements that are not visible or are disabled
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && 
             style.visibility !== 'hidden' && 
             !el.hasAttribute('disabled') &&
             !el.hasAttribute('inert');
    });
  }

  /**
   * Make background content inert to prevent focus and interaction
   * 
   * WCAG 2.4.3 Compliance: Prevents keyboard users from accessing background content
   * 
   * @method makeBackgroundInert
   */
  makeBackgroundInert() {
    // Clear previous inert elements list
    this.inertElements = [];

    // Selectors for background content that should be made inert
    const backgroundSelectors = [
      'main',
      '#MainContent',
      'header',
      'footer',
      '.shopify-section:not(regional-popup-modal)'
    ];

    backgroundSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        // Don't make the modal itself inert
        if (element.contains(this) || element === this) {
          return;
        }
        
        // Only make inert if not already inert
        if (!element.hasAttribute('inert')) {
          element.setAttribute('inert', '');
          // Store reference for restoration
          this.inertElements.push(element);
        }
      });
    });

    // Also make all direct children of body inert (except the modal)
    Array.from(document.body.children).forEach(child => {
      if (child !== this && !child.contains(this) && !child.hasAttribute('inert')) {
        child.setAttribute('inert', '');
        this.inertElements.push(child);
      }
    });
  }

  /**
   * Restore background content by removing inert attribute
   * 
   * @method restoreBackground
   */
  restoreBackground() {
    // Remove inert attribute from all stored elements
    this.inertElements.forEach(element => {
      if (element && element.hasAttribute('inert')) {
        element.removeAttribute('inert');
      }
    });
    
    // Clear the list
    this.inertElements = [];
  }

  getLocaleCodeForCountry(countryCode) {
    // Use dynamic mapping built from Shopify Markets data
    return this.countryToLocaleMap[countryCode] || null;
  }

  /**
   * Build the market URL for a given country code
   * 
   * Constructs a URL with the appropriate locale subdirectory (e.g., /en-gb/, /en-ca/)
   * while preserving the current path and removing any existing locale prefix.
   * 
   * @method buildMarketUrl
   * @param {string} countryCode - ISO country code (e.g., 'GB', 'CA', 'DE')
   * @returns {string} Complete URL with locale subdirectory
   */
  buildMarketUrl(countryCode) {
    // Get locale code for the country (en-ca, en-gb, en-eu)
    const localeCode = this.getLocaleCodeForCountry(countryCode);
    
    if (!localeCode) {
      // If no locale mapping, stay on current URL
      console.warn('Regional Popup: No locale mapping for country', countryCode);
      return window.location.href;
    }

    // Build URL with locale subdirectory
    const currentUrl = new URL(window.location.href);
    let currentPath = currentUrl.pathname;
    
    // Remove any existing locale prefix if present (handle both /en-ca/ and /en-ca patterns)
    // This prevents double prefixes like /en-gb/en-gb
    currentPath = currentPath.replace(/^\/(en-[a-z]{2}|[a-z]{2})(\/|$)/i, '/');
    
    // Ensure path starts with /
    if (!currentPath.startsWith('/')) {
      currentPath = '/' + currentPath;
    }
    
    // Build new path with locale subdirectory
    // Format: /en-ca/path or /en-gb/path or /en-eu/path
    // If currentPath is just '/', we want /en-ca/ (with trailing slash for root)
    const newPath = currentPath === '/' ? `/${localeCode}/` : `/${localeCode}${currentPath}`;
    
    currentUrl.pathname = newPath;
    
    return currentUrl.toString();
  }

  /**
   * Handle "Go to Region" button click
   * 
   * Saves the user's preference and redirects them to their regional market.
   * 
   * @method handleGoToRegion
   * @async
   * @returns {Promise<void>}
   */
  async handleGoToRegion() {
    if (!this.detectedCountry) {
      this.handleBrowseGlobal();
      return;
    }

    // Set preference
    this.setPreference(this.detectedCountry.toLowerCase());

    // Build and navigate to market URL
    const marketUrl = this.buildMarketUrl(this.detectedCountry);
    
    // Redirect
    window.location.href = marketUrl;
  }

  handleBrowseGlobal() {
    // Set preference to browse global
    this.setPreference('browse_global');
    
    // Hide popup
    this.hide();
  }

  /**
   * Show the regional popup modal
   * 
   * WCAG Compliance:
   * - 4.1.2: Validates that no placeholder text remains before making the modal visible
   * - 2.4.7 Focus Visible: Automatically focuses the close button when modal opens
   * - 2.4.3 Focus Order: Traps focus within modal and makes background inert
   * - 2.1.2 No Keyboard Trap: Allows escape via Escape key
   * 
   * The modal is kept aria-hidden until this point to prevent screen readers from
   * accessing placeholder text. Focus is immediately moved to the first focusable
   * element (close button) to ensure keyboard and screen reader users are aware
   * the modal has appeared.
   * 
   * @method show
   * @returns {void}
   */
  show() {
    // Final validation: Ensure no placeholders remain before showing modal
    // This is a safety check in case updatePopupContent() was bypassed
    if (!this.validatePlaceholdersReplaced()) {
      console.error('Regional Popup: Cannot show modal - placeholders detected. This is a WCAG 4.1.2 violation.');
      return;
    }
    
    // Store the currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement;
    
    // Make background content inert BEFORE showing modal
    // This prevents keyboard users from accessing background content (WCAG 2.4.3)
    this.makeBackgroundInert();
    
    // Remove aria-hidden to make modal accessible to screen readers
    // (It was set initially in the HTML to prevent screen readers from accessing placeholder text)
    this.removeAttribute('aria-hidden');
    
    // Add open attribute
    this.setAttribute('open', '');
    
    // Lock scroll
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('has-open-modal');
    document.body.classList.add('has-open-dialog');
    
    // WCAG 2.4.7 Focus Visible: Focus the close button when modal opens
    // This ensures keyboard and screen reader users immediately know the modal has appeared
    // Use requestAnimationFrame to ensure DOM is ready, then setTimeout to ensure modal is visible
    const closeButton = this.querySelector('#RegionalPopupClose');
    
    if (closeButton) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Focus the close button (first focusable element)
          closeButton.focus();
          
          // Also use theme's trapFocus if available for additional safety
          // Our custom handleKeydown will handle the focus trap, but trapFocus provides extra layer
          if (typeof trapFocus === 'function') {
            try {
              trapFocus(this, closeButton);
            } catch (e) {
              // If trapFocus fails, our handleKeydown will still work
              console.warn('Regional Popup: trapFocus failed, using custom focus trap', e);
            }
          }
        }, 100);
      });
    }

    // Make background inert for screen readers (aria-hidden)
    const mainContent = document.querySelector('#MainContent');
    if (mainContent) {
      mainContent.setAttribute('aria-hidden', 'true');
    }
  }

  hide() {
    // Remove open attribute
    this.removeAttribute('open');
    
    // Re-add aria-hidden to prevent screen readers from accessing modal when hidden
    // This ensures screen readers don't encounter placeholder text if modal is re-shown
    this.setAttribute('aria-hidden', 'true');
    
    // Restore background content (remove inert attribute)
    // This allows keyboard users to access background content again (WCAG 2.4.3)
    this.restoreBackground();
    
    // Unlock scroll
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('has-open-modal');
    document.body.classList.remove('has-open-dialog');
    
    // Remove focus trap (theme's trapFocus if it was used)
    if (typeof removeTrapFocus === 'function') {
      try {
        removeTrapFocus();
      } catch (e) {
        // Ignore errors
      }
    }

    // Restore background for screen readers (remove aria-hidden)
    const mainContent = document.querySelector('#MainContent');
    if (mainContent) {
      mainContent.removeAttribute('aria-hidden');
    }
    
    // Restore focus to previously focused element or body
    // This ensures keyboard users return to where they were (WCAG 2.4.3)
    requestAnimationFrame(() => {
      if (this.previouslyFocusedElement && 
          document.contains(this.previouslyFocusedElement) &&
          typeof this.previouslyFocusedElement.focus === 'function') {
        try {
          this.previouslyFocusedElement.focus();
        } catch (e) {
          // If focusing fails, focus body as fallback
          document.body.focus();
        }
      } else {
        // Fallback: focus body if no previous element
        document.body.focus();
      }
      this.previouslyFocusedElement = null;
    });
  }

  /**
   * Cleanup when element is removed from DOM
   * 
   * @method disconnectedCallback
   */
  disconnectedCallback() {
    // Remove event listeners
    this.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    
    // Restore background if modal was open when removed
    if (this.hasAttribute('open')) {
      this.restoreBackground();
    }
    
    // Clear references
    this.inertElements = [];
    this.previouslyFocusedElement = null;
  }
}

// Register custom element
customElements.define('regional-popup-modal', RegionalPopupModal);

// Listen for market changes from footer selector
document.addEventListener('DOMContentLoaded', () => {
  // Clear popup preference when user manually changes market
  const localizationForms = document.querySelectorAll('form[action*="localization"]');
  localizationForms.forEach(form => {
    form.addEventListener('submit', () => {
      if (typeof Cookies !== 'undefined') {
        Cookies.remove('regional_popup_dismissed');
      }
      try {
        localStorage.removeItem('regional_popup_dismissed');
      } catch (e) {
        // Ignore errors
      }
    });
  });
  
  // Development-time validation: Check for unreplaced placeholders
  // This helps catch WCAG 4.1.2 violations during development
  // Check for placeholders after a short delay to allow JavaScript to run
  setTimeout(() => {
    const modal = document.querySelector('regional-popup-modal');
    if (modal) {
      const modalText = modal.textContent || '';
      const placeholderPattern = /\[\[\s*detected_region\s*\]\]/i;
      
      if (placeholderPattern.test(modalText)) {
        console.warn('⚠️ Regional Popup WCAG Warning: Unreplaced placeholders detected in modal content!');
        console.warn('This could cause screen readers to announce placeholder text (WCAG 4.1.2 violation).');
        console.warn('Check that region detection and text replacement is working correctly.');
      }
    }
  }, 2000);
});
